import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { isAuthenticated, logout } from '../utils/auth';
import PageHeader from './PageHeader';
import './Groups.css';

const fetchOptions = { credentials: 'include', headers: { 'Content-Type': 'application/json' } };

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newGroupName, setNewGroupName] = useState('');
  const [groupLeaderboards, setGroupLeaderboards] = useState({});
  const [memberPicks, setMemberPicks] = useState({});
  const [inviteEmails, setInviteEmails] = useState({});
  const [inviteMessages, setInviteMessages] = useState({});
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const navigate = useNavigate();

  const handleUnauthorized = useCallback(() => {
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    logout();
    navigate('/login');
  }, [navigate]);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await fetch('/api/groups', fetchOptions);
      if (res.status === 401) { handleUnauthorized(); return; }
      const data = await res.json();
      if (Array.isArray(data)) {
        setGroups(data);
      } else {
        console.error('Unexpected response from /api/groups:', data);
        setGroups([]);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  }, [handleUnauthorized]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    const init = async () => {
      try {
        const yearsRes = await fetch('/api/races/years', fetchOptions);
        if (yearsRes.ok) {
          const years = await yearsRes.json();
          setAvailableYears(years);
          setSelectedYear(years.length > 0 ? years[0] : null);
        }
      } catch (err) {
        console.error('Error fetching years:', err);
      }
      fetchGroups();
    };
    init();
  }, [navigate, fetchGroups]);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    try {
      const res = await fetch('/api/groups', {
        method: 'POST',
        ...fetchOptions,
        body: JSON.stringify({ name: newGroupName.trim() }),
      });
      if (res.status === 401) { handleUnauthorized(); return; }
      if (res.ok) {
        setNewGroupName('');
        fetchGroups();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to create group.');
      }
    } catch (err) {
      console.error('Error creating group:', err);
    }
  };

  const handleViewLeaderboard = async (groupId) => {
    if (groupLeaderboards[groupId]) {
      setGroupLeaderboards(prev => { const n = { ...prev }; delete n[groupId]; return n; });
      return;
    }
    try {
      const yearParam = selectedYear ? `?year=${selectedYear}` : '';
      const res = await fetch(`/api/groups/${groupId}/leaderboard${yearParam}`, fetchOptions);
      if (res.status === 401) { handleUnauthorized(); return; }
      const data = await res.json();
      setGroupLeaderboards(prev => ({ ...prev, [groupId]: data }));
    } catch (err) {
      console.error('Error fetching group leaderboard:', err);
    }
  };

  const handleViewMemberPicks = async (groupId, memberId) => {
    const key = `${groupId}-${memberId}`;
    if (memberPicks[key]) {
      setMemberPicks(prev => { const n = { ...prev }; delete n[key]; return n; });
      return;
    }
    try {
      const res = await fetch(`/api/groups/${groupId}/member-picks/${memberId}`, fetchOptions);
      if (res.status === 401) { handleUnauthorized(); return; }
      const data = await res.json();
      setMemberPicks(prev => ({ ...prev, [key]: data }));
    } catch (err) {
      console.error('Error fetching member picks:', err);
    }
  };

  const handleInvite = async (e, groupId) => {
    e.preventDefault();
    const email = inviteEmails[groupId] || '';
    if (!email.trim()) return;
    try {
      const res = await fetch(`/api/groups/${groupId}/invite`, {
        method: 'POST',
        ...fetchOptions,
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.status === 401) { handleUnauthorized(); return; }
      const data = await res.json();
      if (res.ok) {
        setInviteMessages(prev => ({ ...prev, [groupId]: '✅ Invited successfully!' }));
        setInviteEmails(prev => ({ ...prev, [groupId]: '' }));
        fetchGroups();
      } else {
        setInviteMessages(prev => ({ ...prev, [groupId]: `❌ ${data.error || 'Failed to invite.'}` }));
      }
    } catch (err) {
      console.error('Error inviting user:', err);
    }
  };

  if (loading) {
    return <div className="groups"><p>Loading groups...</p></div>;
  }

  return (
    <div className="groups">
      <div className="groups-header">
        <PageHeader overline="Friends & Competitions" title="MY GROUPS" />
        <button onClick={() => navigate('/dashboard')} className="pick-button" style={{ maxWidth: 180 }}>
          ← Back to Dashboard
        </button>
      </div>

      <div className="groups-create-card">
        <h2>Create a New Group</h2>
        <form onSubmit={handleCreateGroup} className="groups-create-form">
          <input
            type="text"
            className="groups-input"
            placeholder="Group name"
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
          />
          <button type="submit" className="pick-button" style={{ maxWidth: 160 }}>
            Create Group
          </button>
        </form>
      </div>

      {availableYears.length > 0 && (
        <div className="season-selector">
          <span className="season-label">Season:</span>
          {availableYears.map(year => (
            <button
              key={year}
              className={`season-btn${selectedYear === year ? ' season-btn--active' : ''}`}
              onClick={() => { setSelectedYear(year); setGroupLeaderboards({}); }}
            >
              {year}
            </button>
          ))}
          <button
            className={`season-btn${selectedYear === null ? ' season-btn--active' : ''}`}
            onClick={() => { setSelectedYear(null); setGroupLeaderboards({}); }}
          >
            All
          </button>
        </div>
      )}

      {groups.length === 0 ? (
        <p className="groups-empty">You are not in any groups yet. Create one above!</p>
      ) : (
        <div className="groups-list">
          {groups.map(group => (
            <div key={group._id} className="group-card">
              <div className="group-card-header">
                <h2>{group.name}</h2>
                <button
                  onClick={() => handleViewLeaderboard(group._id)}
                  className="pick-button"
                  style={{ maxWidth: 180 }}
                >
                  {groupLeaderboards[group._id] ? 'Hide Leaderboard' : 'View Leaderboard'}
                </button>
              </div>

              {groupLeaderboards[group._id] && (
                <div className="group-leaderboard">
                  <h3>🏆 Group Leaderboard</h3>
                  <table className="glb-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Player</th>
                        <th>Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupLeaderboards[group._id].map(entry => (
                        <tr key={entry.rank} className="glb-row">
                          <td className="glb-rank">
                            {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`}
                          </td>
                          <td className="glb-player">
                            {entry.avatar && <img src={entry.avatar} alt={entry.name} className="glb-avatar" referrerPolicy="no-referrer" />}
                            <span>{entry.name}</span>
                          </td>
                          <td className="glb-points">{entry.totalPoints}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="group-members">
                <h3>Members ({group.members?.length || 0})</h3>
                <div className="members-list">
                  {(group.members || []).map(member => {
                    const key = `${group._id}-${member._id}`;
                    const picks = memberPicks[key];
                    return (
                      <div key={member._id} className="member-item-wrapper">
                        <div className="member-item">
                          {member.avatar && <img src={member.avatar} alt={member.name} className="member-avatar" referrerPolicy="no-referrer" />}
                          <span>{member.name}</span>
                          <button
                            className="view-picks-btn"
                            onClick={() => handleViewMemberPicks(group._id, member._id)}
                          >
                            {picks ? 'Hide Picks' : 'View Picks'}
                          </button>
                        </div>
                        {picks && (
                          <div className="member-picks-section">
                            {picks.length === 0 ? (
                              <p className="member-picks-empty">No completed race picks yet.</p>
                            ) : (
                              picks.map(pick => (
                                <div key={pick._id} className="member-pick-card">
                                  <div className="member-pick-race">
                                    <Link to={`/races/${pick.raceId._id}`} className="member-pick-race-link">
                                      {pick.raceId.name}
                                    </Link>
                                    <span className="member-pick-date">
                                      {pick.raceId.startTime ? new Date(pick.raceId.startTime).toLocaleDateString() : ''}
                                    </span>
                                  </div>
                                  <div className="member-pick-podium">
                                    <div className="member-pick-col">
                                      <span className="member-pick-label">🥇 Pick</span>
                                      {pick.firstPlace ? (
                                        <Link to={`/drivers/${pick.firstPlace._id}`} className="member-pick-driver">
                                          {pick.firstPlace.name}
                                        </Link>
                                      ) : <span>—</span>}
                                    </div>
                                    <div className="member-pick-col">
                                      <span className="member-pick-label">🥈 Pick</span>
                                      {pick.secondPlace ? (
                                        <Link to={`/drivers/${pick.secondPlace._id}`} className="member-pick-driver">
                                          {pick.secondPlace.name}
                                        </Link>
                                      ) : <span>—</span>}
                                    </div>
                                    <div className="member-pick-col">
                                      <span className="member-pick-label">🥉 Pick</span>
                                      {pick.thirdPlace ? (
                                        <Link to={`/drivers/${pick.thirdPlace._id}`} className="member-pick-driver">
                                          {pick.thirdPlace.name}
                                        </Link>
                                      ) : <span>—</span>}
                                    </div>
                                    <div className="member-pick-col">
                                      <span className="member-pick-label">Result 1st</span>
                                      {pick.raceId.results?.first ? (
                                        <Link to={`/drivers/${pick.raceId.results.first._id}`} className="member-pick-driver">
                                          {pick.raceId.results.first.name}
                                        </Link>
                                      ) : <span>—</span>}
                                    </div>
                                    <div className="member-pick-col">
                                      <span className="member-pick-label">Result 2nd</span>
                                      {pick.raceId.results?.second ? (
                                        <Link to={`/drivers/${pick.raceId.results.second._id}`} className="member-pick-driver">
                                          {pick.raceId.results.second.name}
                                        </Link>
                                      ) : <span>—</span>}
                                    </div>
                                    <div className="member-pick-col">
                                      <span className="member-pick-label">Result 3rd</span>
                                      {pick.raceId.results?.third ? (
                                        <Link to={`/drivers/${pick.raceId.results.third._id}`} className="member-pick-driver">
                                          {pick.raceId.results.third.name}
                                        </Link>
                                      ) : <span>—</span>}
                                    </div>
                                    <div className="member-pick-col member-pick-points-col">
                                      <span className="member-pick-label">Points</span>
                                      <span className="member-pick-points">{pick.points ?? 0}</span>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="group-invite">
                <h3>Invite by Email</h3>
                <form onSubmit={(e) => handleInvite(e, group._id)} className="invite-form">
                  <input
                    type="email"
                    className="groups-input"
                    placeholder="Email address"
                    value={inviteEmails[group._id] || ''}
                    onChange={e => setInviteEmails(prev => ({ ...prev, [group._id]: e.target.value }))}
                  />
                  <button type="submit" className="pick-button" style={{ maxWidth: 100 }}>
                    Invite
                  </button>
                </form>
                {inviteMessages[group._id] && (
                  <p className="invite-message">{inviteMessages[group._id]}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Groups;
